import { BookingAddOnFormResponse, FormAnswerValue } from './types';

/*
 * ⭐ THE ONE PLACE the vendor form's answers are encoded for the wire. RES-1057.
 *
 * ⛔ WHY A STRIP EXISTS AT ALL — the renderer hands back MORE than the tenant answered.
 * forms-admin's stateless `onChange` emits the COMPLETE normalised map for every field in the
 * definition, seeded from its own `DEFAULT_VALUES`: `number`/`date`/`time` seed to `null`, `select`
 * and `file` seed to `[]`. So an almost-empty form arrives carrying five kinds of not-an-answer that
 * nobody typed, and both of those values are actively dangerous downstream:
 *
 *   - `null`  matches NO branch in work-orders' `#mapFormValuesToGrpc` — an if/else-if chain with no
 *             `else` — so the key is never assigned and the answer reaches the vendor MISSING, with
 *             no throw, no log and no metric.
 *   - `[]`    can never be a file list: `#isFileInputArray` requires `length > 0`, so an empty array
 *             is classified as a select. An unanswered upload and an unanswered multi-select become
 *             indistinguishable.
 *
 * An unanswered field must therefore be an OMITTED KEY. helix refuses `null` at the boundary (422)
 * precisely so this function cannot produce one.
 *
 * ⛔ REJECTED: also dropping `''`, `0` and `false`. They look like emptiness and are not — helix's
 * `isAddOnFormFieldValue` accepts all three DELIBERATELY, as real answers. Dropping `false` would
 * silently discard every unticked checkbox a tenant meant to leave unticked, and dropping `0` would
 * discard a headcount of zero. Empty array is the single exception, and only because it is ambiguous
 * rather than because it is empty.
 *
 * ⚠️ File entries pass through untouched and MUST keep the key names the renderer emits —
 * `fileUuid`, `fileName`, `fileSizeBytes`. The consumer decides an array is a file list by testing
 * for the literal key `fileUuid` on its FIRST element only; an array shaped `{ uuid, name, size }`
 * is not dropped but mis-sent as a select, carrying objects where strings belong.
 */

const isEmptyArray = (value: unknown): boolean => Array.isArray(value) && value.length === 0;

const isAnswered = (value: unknown): value is FormAnswerValue =>
  value !== null && value !== undefined && !isEmptyArray(value);

export const encodeFormAnswers = (values: Record<string, unknown> | undefined): Record<string, FormAnswerValue> =>
  Object.fromEntries(Object.entries(values ?? {}).filter(([, value]) => isAnswered(value))) as Record<
    string,
    FormAnswerValue
  >;

/*
 * ⚠️ `values` may be undefined rather than empty. The renderer emits nothing at all until the first
 * `updateValue`, so a tenant who opens the form and submits without touching it never produces a map.
 * That is a form answered with nothing, not an absent form — it still travels, so the template uuid
 * records which questions were put to them.
 */
export const buildFormResponse = (
  formTemplateUuid: string,
  values: Record<string, unknown> | undefined,
): BookingAddOnFormResponse => ({
  form_template_uuid: formTemplateUuid,
  values: encodeFormAnswers(values),
});
